// src/components/DisciplineOfficerNotes.js - RULING 305.9 (Record 637): ruling 305.7 on a discipline case.
// The officers' notes on a case, in disciplineCases/{caseId}/officerNotes. An officer (or the administrator) reads every
// one and writes them, with the warning above the box; the member the case names reads only those the server has shown
// him (visibleToMember) - his query must say so, or the rules refuse the list. Every decision is in
// src\data\disciplineOfficerNotes.js and src\data\officerNotes.js; this file renders and writes.
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, onSnapshot } from 'firebase/firestore';
import { newOfficerNote, toMs } from '../data/officerNotes';
import { DISCIPLINE_OFFICER_NOTE_WARNING, disciplineVisibilityLine, writesDisciplineOfficerNotes } from '../data/disciplineOfficerNotes';

export default function DisciplineOfficerNotes({ caseId, disciplineCase, user }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState('');
  const [shareNow, setShareNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const writes = writesDisciplineOfficerNotes(user, disciplineCase);
  const status = disciplineCase ? disciplineCase.status : null;

  useEffect(() => {
    if (!caseId || !user || !status || status === 'draft') { setRows([]); return undefined; }
    const col = collection(db, 'disciplineCases', caseId, 'officerNotes');
    const q = writes ? col : query(col, where('visibleToMember', '==', true));
    return onSnapshot(q, snap => setRows(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => setRows([]));
  }, [caseId, user, status, writes]);

  if (!status || status === 'draft') return null;
  if (!writes && rows.length === 0) return null;   // the member sees the card only once a note has reached him

  const save = async () => {
    setErr('');
    const r = newOfficerNote(user, text, shareNow, serverTimestamp());
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, 'disciplineCases', caseId, 'officerNotes'), r.doc);
      setText(''); setShareNow(false);
    } catch (e) {
      setErr('The note was NOT saved: ' + ((e && e.message) || 'the write was refused') + '. Your text is still in the box.');
    } finally {
      setSaving(false);
    }
  };

  const sorted = rows.slice().sort((a, b) => (toMs(a.addedAt) || 0) - (toMs(b.addedAt) || 0));
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title" style={{ marginBottom: 8 }}>{writes ? 'Officers’ Notes' : 'Notes from your representatives'}</div>
      <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 12 }}>
        {writes
          ? 'Permanent record - no edits, no deletions. These stay between the union’s officers until a decision is recorded on the case or it is resolved, or 30 days after each is written - whichever comes first - and then the member reads them.'
          : 'Permanent record. Your representatives’ notes on your case, as they have been released to you.'}
      </div>
      {sorted.length === 0 ? (
        <div style={{ color: 'var(--steel)', fontSize: 13, padding: '8px 0' }}>No officer notes yet.</div>
      ) : sorted.map(n => (
        <div key={n.id} style={{ background: '#0A1628', border: '1px solid ' + (n.visibleToMember ? '#2E4A6B' : '#8A6A20'), borderRadius: 6, padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: n.role === 'general_chair' ? '#E8A020' : '#4A9EE8', textTransform: 'uppercase', letterSpacing: 1, border: '1px solid currentColor', borderRadius: 3, padding: '1px 6px' }}>
              {n.role === 'general_chair' ? 'General Chair' : n.role === 'local_chair' ? 'Local Chair' : 'Administrator'}
            </span>
            <span style={{ fontSize: 11, color: '#8AA4BF' }}>{n.addedBy}{toMs(n.addedAt) ? ' · ' + new Date(toMs(n.addedAt)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
          </div>
          <div style={{ fontSize: 13, color: '#F4F6F8', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.text}</div>
          {writes ? <div style={{ fontSize: 11, color: n.visibleToMember ? '#8AA4BF' : '#E8A020', marginTop: 6 }}>{disciplineVisibilityLine(n)}</div> : null}
        </div>
      ))}
      {writes ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#F4F6F8', background: '#3A2A08', border: '1px solid #E8A020', borderRadius: 6, padding: '8px 12px', marginBottom: 8, lineHeight: 1.5 }}>
            <b style={{ color: '#E8A020' }}>Read this before you write.</b> {DISCIPLINE_OFFICER_NOTE_WARNING}
            <label style={{ display: 'block', marginTop: 6, cursor: 'pointer' }}>
              <input aria-label="Share this note with the member now" type="checkbox" checked={shareNow} onChange={e => setShareNow(e.target.checked)} style={{ marginRight: 6 }} />
              Share this note with the member now
            </label>
          </div>
          <textarea aria-label="Share this note with the member now" className="form-textarea" rows={3} value={text} onChange={e => setText(e.target.value)} placeholder="An officer's note on this case..." />
          {err ? <div style={{ color: '#E85A4A', fontSize: 12, marginTop: 6 }}>{err}</div> : null}
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={save} disabled={saving || !text.trim()}>
              {saving ? 'Saving...' : 'Add Officer Note'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
